import { useEffect, useMemo, useState } from "react";
import type { DmChannel, Relationship, User } from "@majlis/protocol/types";
import { get, post, put, del } from "../lib/api.ts";
import { t } from "../lib/i18n.ts";
import { navigate } from "../lib/router.ts";
import {
  useStore, getState, toast, errText, openDm, dmName, dmOrder, isUnread, mentionsIn, isMuted, type State,
} from "../lib/store.ts";
import { Avatar, Icon, Modal, Confirm, displayName, Field } from "./common.tsx";

const status = (st: State, id: string) => st.presences[id] ?? "offline";

// ---------- sidebar for the home view ----------
export function HomeSidebar({ activeDm, onNewGroup }: { activeDm: string | null; onNewGroup: () => void }) {
  const st = useStore((s) => s);
  const dmMap = st.dms;
  const dms = useMemo(() => dmOrder(st), [dmMap]); // selectors must return stable values; sort from the stable map
  const incoming = Object.values(st.relationships).filter((r) => r.type === "incoming").length;
  return (
    <>
      <div className="rhead"><h1>{t("appName")}</h1></div>
      <div className="rlist">
        <a className="ch" href="/channels/@me" aria-current={!activeDm ? "page" : undefined} onClick={(e) => { e.preventDefault(); navigate("/channels/@me"); }}>
          <Icon name="users" size={18} /><span>{t("friends")}</span>{incoming > 0 && <span className="badge">{incoming}</span>}</a>
        <div className="cat"><span>{t("directMessages")}</span>
          <button className="ib xs" onClick={onNewGroup} aria-label={t("newGroup")} title={t("newGroup")}><Icon name="plus" size={14} /></button></div>
        {dms.length === 0 && <p className="muted small pad">{t("noDms")}</p>}
        {dms.map((dm) => {
          const unread = !isMuted(st, dm.id) && isUnread(st, dm.id, dm.lastMessageId);
          const n = isMuted(st, dm.id) ? 0 : mentionsIn(st, dm.id);
          const other = dm.recipients[0];
          return (
            <div key={dm.id} className="chrow">
              <a className={`ch dm${unread ? " unread" : ""}`} href={`/channels/@me/${dm.id}`} aria-current={activeDm === dm.id ? "page" : undefined}
                onClick={(e) => { e.preventDefault(); navigate(`/channels/@me/${dm.id}`); }}>
                {dm.kind === "dm" && other ? <Avatar user={other} size={30} status={status(st, other.id)} /> : <span className="grpav"><Icon name="users" size={16} /></span>}
                <span className="dmname"><span dir="auto">{dmName(dm)}</span>{dm.kind === "group_dm" && <small>{t("membersCount", { n: dm.recipients.length + 1 })}</small>}</span>
                {n > 0 && <span className="badge">{n}</span>}
              </a>
              <button className="ib xs gear" onClick={() => closeDm(dm)} aria-label={dm.kind === "dm" ? t("closeDm") : t("leaveGroup")} title={dm.kind === "dm" ? t("closeDm") : t("leaveGroup")}><Icon name="x" size={14} /></button>
            </div>
          );
        })}
      </div>
    </>
  );
}

async function closeDm(dm: DmChannel) {
  try { await del(`/users/@me/channels/${dm.id}`); if (location.pathname.endsWith(dm.id)) navigate("/channels/@me"); } catch (e) { toast(errText(e)); }
}

// ---------- friends page ----------
type Tab = "online" | "all" | "pending" | "blocked" | "add";

export function FriendsPage({ onMenu, onProfile, welcome }: { onMenu: () => void; onProfile: (id: string) => void; welcome?: React.ReactNode }) {
  const rels = useStore((s) => s.relationships);
  const presences = useStore((s) => s.presences);
  const [tab, setTab] = useState<Tab>("online");
  const [confirm, setConfirm] = useState<{ kind: "remove" | "block"; user: User } | null>(null);
  const list = Object.values(rels);
  const shown = list.filter((r) =>
    tab === "online" ? r.type === "friend" && (presences[r.user.id] ?? "offline") !== "offline"
      : tab === "all" ? r.type === "friend"
        : tab === "pending" ? r.type === "incoming" || r.type === "outgoing"
          : tab === "blocked" ? r.type === "blocked" : false)
    .sort((a, b) => displayName(a.user).localeCompare(displayName(b.user)));
  const pendingN = list.filter((r) => r.type === "incoming").length;
  const act = async (fn: () => Promise<unknown>) => { try { await fn(); } catch (e) { toast(errText(e)); } };
  const empty = { online: "noFriendsOnline", all: "noFriends", pending: "noPending", blocked: "noBlocked", add: "noFriends" } as const;
  return (
    <div className="friends">
      <header className="top">
        <button className="ib menu-btn" onClick={onMenu} aria-label={t("openMenu")}><Icon name="menu" /></button>
        <div className="ttl"><Icon name="users" size={20} /><b>{t("friends")}</b></div>
        <nav className="ftabs" role="tablist">
          {(["online", "all", "pending", "blocked"] as const).map((k) => <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)}>
            {t(k)}{k === "pending" && pendingN > 0 && <span className="badge">{pendingN}</span>}</button>)}
          <button role="tab" className="addf" aria-selected={tab === "add"} onClick={() => setTab("add")}>{t("addFriend")}</button>
        </nav>
      </header>
      <div className="flist">
        {tab !== "add" && welcome}
        {tab === "add" ? <AddFriend /> : shown.length === 0 ? (welcome ? null : <p className="muted center pad">{t(empty[tab])}</p>) : shown.map((r) => (
          <div key={r.user.id} className="frow">
            <button className="avbtn" onClick={() => onProfile(r.user.id)} aria-label={displayName(r.user)}><Avatar user={r.user} size={36} status={r.type === "friend" ? presences[r.user.id] ?? "offline" : undefined} /></button>
            <div className="mname"><b dir="auto">{displayName(r.user)}</b>
              <small>{r.type === "friend" ? t(presences[r.user.id] ?? "offline") : r.type === "incoming" ? t("incomingRequest") : r.type === "outgoing" ? t("outgoingRequest") : `@${r.user.username}`}</small></div>
            <div className="row">
              {r.type === "friend" && <>
                <button className="btn sm pri" onClick={() => act(() => openDm(r.user.id))}>{t("message")}</button>
                <button className="ib sm" onClick={() => setConfirm({ kind: "remove", user: r.user })} aria-label={t("removeFriend")} title={t("removeFriend")}><Icon name="x" size={16} /></button></>}
              {r.type === "incoming" && <>
                <button className="btn sm pri" onClick={() => act(() => put(`/users/@me/relationships/${r.user.id}`))}>{t("accept")}</button>
                <button className="btn sm ghost" onClick={() => act(() => del(`/users/@me/relationships/${r.user.id}`))}>{t("decline")}</button></>}
              {r.type === "outgoing" && <button className="btn sm ghost" onClick={() => act(() => del(`/users/@me/relationships/${r.user.id}`))}>{t("cancelRequest")}</button>}
              {r.type === "blocked" && <button className="btn sm ghost" onClick={() => act(() => del(`/users/@me/relationships/${r.user.id}`))}>{t("unblock")}</button>}
            </div>
          </div>))}
      </div>
      {confirm && <Confirm title={confirm.kind === "remove" ? t("removeFriend") : t("block")} confirmLabel={confirm.kind === "remove" ? t("removeFriend") : t("block")}
        body={t(confirm.kind === "remove" ? "removeFriendQ" : "blockQ", { name: displayName(confirm.user) })} onClose={() => setConfirm(null)}
        onConfirm={() => (confirm.kind === "remove" ? del(`/users/@me/relationships/${confirm.user.id}`) : put(`/users/@me/blocks/${confirm.user.id}`))} />}
    </div>
  );
}

function AddFriend() {
  const [name, setName] = useState(""); const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null); const [busy, setBusy] = useState(false);
  return (
    <form className="addfriend stack" onSubmit={async (e) => {
      e.preventDefault(); setBusy(true); setMsg(null);
      const u = name.trim().replace(/^@/, "").toLowerCase();
      try { await post("/users/@me/relationships", { username: u }); setMsg({ ok: true, text: t("requestSent", { name: u }) }); setName(""); }
      catch (er) { setMsg({ ok: false, text: errText(er) }); } finally { setBusy(false); }
    }}>
      <h3>{t("addFriend")}</h3>
      <p className="muted small">{t("addFriendHint")}</p>
      <div className="copyrow"><input id="add-friend" value={name} onChange={(e) => setName(e.target.value)} placeholder="username" dir="ltr" aria-label={t("username")} />
        <button className="btn pri" disabled={busy || name.trim().length < 2}>{t("sendRequest")}</button></div>
      {msg && <p className={msg.ok ? "ok" : "err"} role="status">{msg.text}</p>}
    </form>
  );
}

// ---------- DM side panel ----------
export function DmPanel({ dm, onProfile }: { dm: DmChannel; onProfile: (id: string) => void }) {
  const presences = useStore((s) => s.presences);
  const me = useStore((s) => s.me)!;
  const rels = useStore((s) => s.relationships);
  const [adding, setAdding] = useState(false);
  const people = [me, ...dm.recipients];
  return (
    <aside className="members" aria-label={t("members2")}>
      <div className="cat">{t("members2")} — {people.length}</div>
      {people.map((u) => <button key={u.id} className="mem" onClick={() => onProfile(u.id)}>
        <Avatar user={u} size={32} status={presences[u.id] ?? "offline"} />
        <div><b dir="auto">{displayName(u)}</b><small>{dm.ownerId === u.id ? t("owner") : t(presences[u.id] ?? "offline")}</small></div></button>)}
      {dm.kind === "group_dm" && <button className="btn ghost block sm" onClick={() => setAdding(true)}><Icon name="plus" size={14} /> {t("addToGroup")}</button>}
      {adding && <PickFriends title={t("addToGroup")} exclude={new Set(people.map((u) => u.id))} max={10 - people.length} rels={rels} confirmLabel={t("addToGroup")}
        onClose={() => setAdding(false)} onPick={async (ids) => { for (const id of ids) await put(`/users/@me/channels/${dm.id}/recipients/${id}`); }} />}
    </aside>
  );
}

function PickFriends({ title, exclude, max, rels, confirmLabel, onPick, onClose, withName = false }:
  { title: string; exclude: Set<string>; max: number; rels: Record<string, Relationship>; confirmLabel: string; onPick: (ids: string[], name: string) => Promise<void>; onClose: () => void; withName?: boolean }) {
  const friends = Object.values(rels).filter((r) => r.type === "friend" && !exclude.has(r.user.id));
  const [sel, setSel] = useState<string[]>([]); const [name, setName] = useState(""); const [err, setErr] = useState<string | null>(null); const [busy, setBusy] = useState(false);
  return (
    <Modal title={title} onClose={onClose} footer={<><button className="btn ghost" onClick={onClose}>{t("cancel")}</button>
      <button className="btn pri" disabled={!sel.length || busy} onClick={async () => { setBusy(true); try { await onPick(sel, name.trim()); onClose(); } catch (e) { setErr(errText(e)); setBusy(false); } }}>{confirmLabel}</button></>}>
      {withName && <Field label={t("groupName")}><input id="group-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={100} dir="auto" /></Field>}
      <p className="muted small">{t("selectFriends")}</p>
      {friends.length === 0 ? <p className="muted">{t("noFriends")}</p> : <div className="pick">{friends.map((r) => (
        <label key={r.user.id} className="check"><input type="checkbox" checked={sel.includes(r.user.id)} disabled={!sel.includes(r.user.id) && sel.length >= max}
          onChange={(e) => setSel(e.target.checked ? [...sel, r.user.id] : sel.filter((x) => x !== r.user.id))} />
          <Avatar user={r.user} size={28} /><span dir="auto">{displayName(r.user)}</span></label>))}</div>}
      {err && <p className="err">{err}</p>}
    </Modal>
  );
}

export function NewGroupModal({ onClose }: { onClose: () => void }) {
  const rels = useStore((s) => s.relationships);
  return <PickFriends title={t("newGroup")} exclude={new Set()} max={9} rels={rels} confirmLabel={t("createGroup")} withName onClose={onClose}
    onPick={async (ids, name) => { const g = await post<DmChannel>("/users/@me/channels", { recipientIds: ids, name: name || undefined }); navigate(`/channels/@me/${g.id}`); }} />;
}

// ---------- profile card ----------
export function ProfileModal({ userId, onClose }: { userId: string; onClose: () => void }) {
  const me = useStore((s) => s.me)!;
  const cached = useStore((s) => s.users[userId]);
  const rel = useStore((s) => s.relationships[userId]);
  const st = useStore((s) => s.presences[userId] ?? "offline");
  const [user, setUser] = useState<User | null>(cached ?? null);
  const [confirmBlock, setConfirmBlock] = useState(false);
  useEffect(() => { get<User>(`/users/${userId}`).then(setUser, () => {}); }, [userId]);
  if (!user) return null;
  const self = userId === me.id;
  const run = async (fn: () => Promise<unknown>) => { try { await fn(); } catch (e) { toast(errText(e)); } };
  return (
    <Modal title={t("profileTitle")} onClose={onClose}>
      {user.bannerUrl && <div className="profile-banner" style={{ backgroundImage: `url(${user.bannerUrl})` }} />}
      <div className="profile-card"><Avatar user={user} size={64} status={st} animate /><div><b dir="auto">{displayName(user)}</b><small dir="ltr">@{user.username}</small><small>{t(st)}</small></div></div>
      {user.customStatus && <p className="custom-status" dir="auto">{user.customStatus}</p>}
      {user.bio && <p dir="auto">{user.bio}</p>}
      {rel?.type === "friend" && <p className="muted small">{t("friendsSince", { d: new Date(rel.since).toLocaleDateString() })}</p>}
      {!self && <div className="row">
        {rel?.type !== "blocked" && <button className="btn pri" onClick={() => run(async () => { await openDm(userId); onClose(); })}>{t("message")}</button>}
        {!rel && <button className="btn ghost" onClick={() => run(async () => { await post("/users/@me/relationships", { username: user.username }); toast(t("requestSent", { name: user.username })); })}>{t("addFriend")}</button>}
        {rel?.type === "incoming" && <button className="btn ghost" onClick={() => run(() => put(`/users/@me/relationships/${userId}`))}>{t("accept")}</button>}
        {rel?.type === "outgoing" && <button className="btn ghost" disabled>{t("outgoingRequest")}</button>}
        {rel?.type === "friend" && <button className="btn ghost" onClick={() => run(() => del(`/users/@me/relationships/${userId}`))}>{t("removeFriend")}</button>}
        {rel?.type === "blocked" ? <button className="btn ghost" onClick={() => run(() => del(`/users/@me/relationships/${userId}`))}>{t("unblock")}</button>
          : <button className="btn danger" onClick={() => setConfirmBlock(true)}>{t("block")}</button>}
      </div>}
      {confirmBlock && <Confirm title={t("block")} body={t("blockQ", { name: displayName(user) })} confirmLabel={t("block")} onClose={() => setConfirmBlock(false)}
        onConfirm={() => put(`/users/@me/blocks/${userId}`)} />}
    </Modal>
  );
}

export { getState };
